// list of customer queries and web queries
import React, { useState, useEffect } from 'react';
import { useQuery } from '../../context/QueryContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
    Select,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Badge,
  Divider,
  Tabs,
  Tab,
  Menu,
  Pagination,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import QueryActionDialog from './QueryActionDialog';
import QueryStatus from '../../components/common/QueryStatus';
import QueryType from '../../components/common/QueryType';
import QueryStatusFilter from '../../components/common/QueryStatusFilter';
import { fetchQueryStatusFilters, fetchWebMessageStatusFilters, fetchQueryTypeFilters } from '../../utils/helper';
import api from '../../services/api';

const QueryList = () => {
  const { 
    customerQueries, 
    websiteMessages, 
    loading, 
    error, 
    fetchCustomerQueries, 
    fetchWebsiteMessages,
    updateQueryStatus
  } = useQuery();
  
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [queryTypeFilter, setQueryTypeFilter] = useState('all');
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [queryStatusOptions, setQueryStatusOptions] = useState([]);
  const [queryTypeOptions, setQueryTypeOptions] = useState([]);
  const [webMessageStatusOptions, setWebMessageStatusOptions] = useState([]);
  const [customerQueryTransitions, setCustomerQueryTransitions] = useState({});
  const [webMessageTransitions, setWebMessageTransitions] = useState({});
  const [actionType, setActionType] = useState('');
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [localCustomerQueries, setLocalCustomerQueries] = useState([]);
  const [localWebsiteMessages, setLocalWebsiteMessages] = useState([]);
  const [webQueryMenuAnchor, setWebQueryMenuAnchor] = useState(null);
  const [selectedWebQuery, setSelectedWebQuery] = useState(null);
  const [customerQueryMenuAnchor, setCustomerQueryMenuAnchor] = useState(null);
  const [selectedCustomerQuery, setSelectedCustomerQuery] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      setLoadingFilters(true);
      
      // Fetch query status filter options using helper function
      const queryStatusOptions = await fetchQueryStatusFilters();
      setQueryStatusOptions(queryStatusOptions.data);
      
      // Store customer query transitions
      if (queryStatusOptions.transitions) {
        setCustomerQueryTransitions(queryStatusOptions.transitions);
      }
      
      // Fetch query type filter options using helper function
      const queryTypeOptions = await fetchQueryTypeFilters();
      setQueryTypeOptions(queryTypeOptions);
      
      // Fetch web message status filter options using helper function
      const webMessageStatusOptions = await fetchWebMessageStatusFilters();
      setWebMessageStatusOptions(webMessageStatusOptions.data);
      
      // Store web message transitions
      if (webMessageStatusOptions.transitions) {
        setWebMessageTransitions(webMessageStatusOptions.transitions);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
      // Don't show error for filter options as they're not critical
    } finally {
      setLoadingFilters(false);
    }
  };

  // Get current queries based on active tab
  const getCurrentQueries = () => {
    const queries = activeTab === 0 ? localCustomerQueries : localWebsiteMessages;
    return Array.isArray(queries) ? queries : [];
  };



  // Fetch data on component mount
  useEffect(() => {
    const fetchQueries = async () => {
      const customerData = await fetchCustomerQueries();
      const websiteData = await fetchWebsiteMessages();
      setLocalCustomerQueries(customerData || []);
      setLocalWebsiteMessages(websiteData || []);
    }
    fetchQueries();
    
    // Fetch filter options
    fetchFilterOptions();
  }, []);

  // Show error if any
  useEffect(() => {
    if (error) {
      setSnackbar({
        open: true,
        message: error,
        severity: 'error'
      });
    }
  }, [error]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchTerm('');
    setStatusFilter('all');
    setQueryTypeFilter('all');
    setCurrentPage(1); // Reset to first page when switching tabs
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusFilter = (event) => {
    setStatusFilter(event);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleQueryTypeFilter = (event) => {
    setQueryTypeFilter(event.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleViewCustomer = (accountId) => {
    navigate(`/customers/${accountId}`);
  };


  const handleQueryAction = async (query, action) => {
    setSelectedQuery(query);
    setActionType(action);
    setOpenActionDialog(true);
  };

  const handleWebQueryAction = (event, query) => {
    setSelectedWebQuery(query);
    setWebQueryMenuAnchor(event.currentTarget);
  };

  const handleWebQueryStatusChange = (status) => {
    setWebQueryMenuAnchor(null);
    setSelectedQuery(selectedWebQuery);
    setActionType(status);
    setOpenActionDialog(true);
  };

  const handleWebQueryMenuClose = () => {
    setWebQueryMenuAnchor(null);
    setSelectedWebQuery(null);
  };

  const handleCustomerQueryAction = (event, query) => {
    console.log("Cusotmer query called", event, query);
    setSelectedCustomerQuery(query);
    setCustomerQueryMenuAnchor(event.currentTarget);
  };

  const handleCustomerQueryStatusChange = (status) => {
    setCustomerQueryMenuAnchor(null);
    setSelectedQuery(selectedCustomerQuery);
    
    // Use the status directly as it should already be in the correct backend format
    setActionType(status);
    setOpenActionDialog(true);
  };

  const handleCustomerQueryMenuClose = () => {
    setCustomerQueryMenuAnchor(null);
    setSelectedCustomerQuery(null);
  };

  const handleActionVerification = async (query_id, remarks, status) => {
    try {
      let response;
      
      // Determine if this is a customer query or website message based on activeTab
      if (activeTab === 0) {
        // Customer query
        response = await api.patch(`/customer-query/${query_id}/query/`, {
          status: status,
          remarks: remarks
        });
      } else {
        // Website message
        response = await api.patch(`/website-contact-messages/${query_id}/`, {
          status: status,
          remarks: remarks
        });
      }
      
      setOpenActionDialog(false);
      
      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: response.data.message || `Query ${status.toLowerCase()} successfully`,
          severity: 'success'
        });
        
        // Refresh the table with new data based on active tab
        if (activeTab === 0) {
          const updatedData = await fetchCustomerQueries();
          setLocalCustomerQueries(updatedData || []);
        } else {
          const updatedData = await fetchWebsiteMessages();
          setLocalWebsiteMessages(updatedData || []);
        }
      } else {
        setSnackbar({
          open: true,
          message: response.data.details || 'Failed to process action',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.details || error.message || 'Error processing action',
        severity: 'error'
      });
    }
  };

  const currentQueries = getCurrentQueries();
  
  const filteredQueries = currentQueries.filter(query => {
    const matchesSearch = activeTab === 0 
      ? (query.customer?.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         query.customer?.owner_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         query.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         query.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()))
      : (query.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         query.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         query.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         query.company_name?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || 
                         query.status === statusFilter || 
                         query.status_verbose === statusFilter ||
                         // Check if the filter value matches the label of any status option
                         (activeTab === 0 ? queryStatusOptions : webMessageStatusOptions)
                           .some(option => option.value === statusFilter && option.label === query.status_verbose);
    const matchesQueryType = activeTab === 0 ? (queryTypeFilter === 'all' || query.query_type_verbose === queryTypeFilter) : true;
    
    return matchesSearch && matchesStatus && matchesQueryType;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredQueries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedQueries = filteredQueries.slice(startIndex, endIndex);





  const getQueryTypeOptions = () => {
    if (activeTab === 0) {
      // Customer queries type options
      if (queryTypeOptions.length > 0) {
        return [
          { value: 'all', label: 'All Types' },
          ...queryTypeOptions.map(option => ({
            value: option.label || option.value,
            label: option.label || option.value
          }))
        ];
      }
      return [{ value: 'all', label: 'All Types' }];
    } else {
      // Web messages don't have query types, return empty array
      return [];
    }
  };

  const getCustomerQueryStatusOptions = (currentStatus) => {
    // Get valid transitions from backend data
    const validTransitionCodes = customerQueryTransitions[currentStatus] || [];
    
    // If no transitions available, return empty array
    if (validTransitionCodes.length === 0) {
      return [];
    }
    
    // Filter queryStatusOptions to only include valid transitions and return with labels
    const validTransitions = queryStatusOptions
      .filter(option => validTransitionCodes.includes(option.value))
      .map(option => ({
        value: option.value,
        label: option.label
      }));
    
    return validTransitions;
  };

  const getWebQueryStatusOptions = (currentStatus) => {
    // Get valid transitions from backend data
    const validTransitionCodes = webMessageTransitions[currentStatus] || [];
    
    // If no transitions available, return empty array
    if (validTransitionCodes.length === 0) {
      return [];
    }
    
    // Filter webMessageStatusOptions to only include valid transitions and return with labels
    const validTransitions = webMessageStatusOptions
      .filter(option => validTransitionCodes.includes(option.value))
      .map(option => ({
        value: option.value,
        label: option.label
      }));
    
    return validTransitions;
  };

  // Helper function to check if customer query has valid transitions
  const hasCustomerQueryTransitions = (status) => {
    const validTransitionCodes = customerQueryTransitions[status] || [];
    return validTransitionCodes.length > 0;
  };

  // Helper function to check if web query has valid transitions
  const hasWebQueryTransitions = (status) => {
    const validTransitionCodes = webMessageTransitions[status] || [];
    return validTransitionCodes.length > 0;
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" color="primary" gutterBottom>
          Query Management
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage customer queries and website inquiries
        </Typography>
      </Box>

      {/* Tabs */}
      <Card sx={{ 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ 
            px: 3, 
            pt: 2,
            '& .MuiTab-root': {
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem'
            }
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                Customer Queries
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageIcon />
                Web Queries
              </Box>
            } 
          />
        </Tabs>
      </Card>



      {/* Search and Filters */}
      <Card sx={{ 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder={activeTab === 0 
                  ? "Search by customer name, email, or content..." 
                  : "Search by name, email, or message..."}
                value={searchTerm}
                onChange={handleSearch}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#f8fafc'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <QueryStatusFilter
                value={statusFilter}
                onChange={handleStatusFilter}
                size="small"
                fullWidth
                label="Status"
                options={activeTab === 0 ? queryStatusOptions : webMessageStatusOptions}
                disabled={loadingFilters}
              />

            </Grid>
            {activeTab === 0 && (
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Query Type</InputLabel>
                  <Select
                    value={queryTypeFilter}
                    label="Query Type"
                    onChange={handleQueryTypeFilter}
                    sx={{ borderRadius: 2 }}
                    disabled={loadingFilters}
                  >
                    {getQueryTypeOptions().map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

          </Grid>
        </CardContent>
      </Card>

      {/* Queries Table */}
      <Card sx={{ 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.05)',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {activeTab === 0 ? (
                    <>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Submitted</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Query Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Content</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Actions</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Submitted</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Company</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Message</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Actions</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={activeTab === 0 ? 6 : 6} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <LinearProgress sx={{ width: '100%', maxWidth: 300 }} />
                        <Typography variant="body2" color="textSecondary">
                          Loading {activeTab === 0 ? 'customer queries' : 'website messages'}...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : paginatedQueries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={activeTab === 0 ? 6 : 6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="textSecondary">
                        No {activeTab === 0 ? 'customer queries' : 'website messages'} found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedQueries.map((query) => (
                  <TableRow 
                    key={query.id}
                    onClick={activeTab === 0 ? () => handleViewCustomer(query.customer?.account_id) : undefined} 
                    sx={{ 
                      borderBottom: '1px solid rgba(0,0,0,0.05)',
                      cursor: activeTab === 0 ? 'pointer' : 'default',
                      '&:hover': activeTab === 0 ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' } : {} 
                    }}
                  >
                    {activeTab === 0 ? (
                      // Customer Query Row
                      <>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {query.created_at}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {query.customer?.owner_name?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {query.customer?.owner_name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {query.customer?.owner_email}
                              </Typography>
                              <Typography variant="caption" display="block" color="textSecondary">
                                {query.customer?.company_name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <QueryType
                            query_type={query.query_type}
                            query_type_verbose={query.query_type_verbose}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <QueryStatus
                            status={query.status}
                            status_verbose={query.status_verbose}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                            {query.content}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {hasCustomerQueryTransitions(query.status) ? (
                              <Tooltip title="Change Status">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCustomerQueryAction(e, query);
                                  }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="No status changes available">
                                <IconButton
                                  size="small"
                                  disabled
                                  sx={{ color: 'text.disabled' }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      // Web Query Row
                      <>
                        <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {query.submitted_at}
                              </Typography>
                            </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {query.full_name?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {query.full_name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {query.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {query.company_name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {query.message}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <QueryStatus
                            status={query.status_verbose}
                            status_verbose={query.status_verbose}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {hasWebQueryTransitions(query.status) ? (
                              <Tooltip title="Change Status">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleWebQueryAction(e, query);
                                  }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="No status changes available">
                                <IconButton
                                  size="small"
                                  disabled
                                  sx={{ color: 'text.disabled' }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pagination */}
      {filteredQueries.length > itemsPerPage && (
        <Card sx={{ 
          mt: 2, 
          borderRadius: 3, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.05)'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2} alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredQueries.length)} of {filteredQueries.length} {activeTab === 0 ? 'customer queries' : 'website messages'}
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                    fontWeight: 600
                  }
                }}
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Web Query Status Menu */}
      <Menu
        anchorEl={webQueryMenuAnchor}
        open={Boolean(webQueryMenuAnchor)}
        onClose={handleWebQueryMenuClose}
        // Add debugging

        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            minWidth: 200
          }
        }}
      >
        {selectedWebQuery && (() => {
          console.log("Selected web query", selectedWebQuery);
          const options = getWebQueryStatusOptions(selectedWebQuery.status);
          return options.map((statusOption) => (
            <MenuItem
              key={statusOption.value}
              onClick={() => handleWebQueryStatusChange(statusOption.value)}
              sx={{
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)'
                }
              }}
            >
              <Typography variant="body2" fontWeight="medium">
                {statusOption.label}
              </Typography>
            </MenuItem>
          ));
        })()}
      </Menu>

      {/* Customer Query Status Menu */}
      <Menu
        anchorEl={customerQueryMenuAnchor}
        open={Boolean(customerQueryMenuAnchor)}
        onClose={handleCustomerQueryMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            minWidth: 200
          }
        }}
      >
        {selectedCustomerQuery && (() => {
          const options = getCustomerQueryStatusOptions(selectedCustomerQuery.status);
          return options.map((statusOption) => (
            <MenuItem
              key={statusOption.value}
              onClick={() => handleCustomerQueryStatusChange(statusOption.value)}
              sx={{
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)'
                }
              }}
            >
              <Typography variant="body2" fontWeight="medium">
                {statusOption.label}
              </Typography>
            </MenuItem>
          ));
        })()}
      </Menu>

      {/* Query Action Dialog */}
      <QueryActionDialog
        open={openActionDialog}
        onClose={() => setOpenActionDialog(false)}
        query={selectedQuery}
        actionType={actionType}
        onAction={handleActionVerification}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QueryList;